import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/**
 * Per-placeholder metadata carried on the decoration spec. The `id` is the same local upload id
 * we surface on the pending pill row, so the editor and the pill row reference the same in-flight
 * upload by the same key. `fileName` powers the aria-label so screen readers announce "Uploading
 * <name>".
 */
export interface UploadPlaceholderSpec {
  readonly id: string;
  readonly fileName: string;
  /** Optional object URL of the chosen file for an immediate thumbnail preview. */
  readonly previewUrl?: string;
}

interface AddMeta {
  readonly kind: 'add';
  readonly pos: number;
  readonly spec: UploadPlaceholderSpec;
}

interface RemoveMeta {
  readonly kind: 'remove';
  readonly id: string;
}

type PluginMeta = AddMeta | RemoveMeta;

const PLUGIN_KEY = new PluginKey<DecorationSet>('comments-upload-placeholder');

/**
 * Resolves a placeholder's current position by id. Returns `null` when the placeholder is no
 * longer in the set — the user removed the pill, the upload was cancelled, or the document was
 * re-set wholesale. Callers must handle the null case as "no-op, the placeholder is gone".
 */
export function findUploadPlaceholderPos(state: EditorState, id: string): number | null {
  const set = PLUGIN_KEY.getState(state);
  if (!set) return null;
  const decos = set.find(undefined, undefined, (s) => (s as UploadPlaceholderSpec).id === id);
  const first = decos[0];
  return first ? first.from : null;
}

/**
 * Builds the widget DOM. Inline-flow span so it sits cleanly inside a paragraph next to the
 * cursor — matches where the eventual `<img>` lands when the document settles. The CSS class
 * does the visible work (skeleton bar / spinner / optional thumbnail); this file only owns the
 * DOM contract.
 */
function renderPlaceholder(spec: UploadPlaceholderSpec): HTMLElement {
  const root = globalThis.document.createElement('span');
  root.className = 'tf-image-upload-placeholder';
  root.setAttribute('contenteditable', 'false');
  root.setAttribute('role', 'img');
  root.setAttribute('aria-label', `Uploading ${spec.fileName}`);
  root.dataset['uploadId'] = spec.id;

  if (spec.previewUrl) {
    const preview = globalThis.document.createElement('img');
    preview.className = 'tf-image-upload-placeholder__preview';
    // `decoding="async"` keeps the browser from decoding the full raster file on the main
    // thread when the `<img>` is inserted. Without this, dropping a multi-MB image freezes
    // the editor for 1-2 s while the decoder runs synchronously before paint. The CSS
    // checkerboard backdrop fills the 96×96 box in the meantime so the placeholder still has
    // visible weight.
    preview.decoding = 'async';
    preview.src = spec.previewUrl;
    preview.alt = '';
    root.append(preview);
  }

  const spinner = globalThis.document.createElement('span');
  spinner.className = 'tf-image-upload-placeholder__spinner';
  spinner.setAttribute('aria-hidden', 'true');
  root.append(spinner);

  return root;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    uploadPlaceholder: {
      /**
       * Inserts an upload placeholder widget at [pos]. The widget renders DOM-only — no real
       * node, no markdown serialization, no document mutation — so the surrounding text stays
       * exactly where it was. ProseMirror's transaction mapping tracks the position through
       * subsequent edits.
       */
      setUploadPlaceholder: (args: { pos: number; spec: UploadPlaceholderSpec }) => ReturnType;

      /**
       * Replaces the placeholder identified by [id] with the supplied image node attributes.
       * No-op when the placeholder is gone (cancelled, removed, doc reset). The image lands at
       * the placeholder's *current* (mapped) position, regardless of how the user has edited
       * the document since the placeholder was added.
       */
      replaceUploadPlaceholder: (args: {
        id: string;
        attrs: { src: string; alt: string };
      }) => ReturnType;

      /**
       * Removes the placeholder identified by [id] without inserting an image. Used on upload
       * failure and when the user removes the corresponding pill mid-upload.
       */
      removeUploadPlaceholder: (args: { id: string }) => ReturnType;
    };
  }
}

/**
 * Tiptap extension that owns a `DecorationSet` of in-flight upload placeholders. Lifecycle:
 *
 *  1. **Add** — drop / paste / picker grabs the intended insert position and dispatches
 *     `setUploadPlaceholder`. A widget decoration appears at that position immediately; the
 *     user gets visual feedback while the upload is still running.
 *  2. **Track** — every transaction's `mapping` is applied to the `DecorationSet`. The
 *     placeholder's position follows surrounding edits (typing, deletes, pastes) automatically.
 *  3. **Replace** — when the upload resolves, `replaceUploadPlaceholder` finds the placeholder's
 *     current mapped position and inserts the real image node there atomically (one transaction
 *     that both removes the widget and inserts the node, so the user sees a single visual swap).
 *  4. **Remove** — failure / cancel / pill-remove dispatches `removeUploadPlaceholder`. The
 *     widget disappears; no image is inserted.
 *
 * Replaces the older `insertContentAt(state.selection.to)` approach which lost the target
 * position whenever the user typed during the upload window.
 */
export const UploadPlaceholder = Extension.create({
  name: 'uploadPlaceholder',

  addProseMirrorPlugins() {
    return [buildUploadPlaceholderPlugin()];
  },

  addCommands() {
    return {
      setUploadPlaceholder:
        ({ pos, spec }) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const meta: AddMeta = { kind: 'add', pos, spec };
            tr.setMeta(PLUGIN_KEY, meta);
            dispatch(tr);
          }
          return true;
        },

      replaceUploadPlaceholder:
        ({ id, attrs }) =>
        ({ state, tr, dispatch, editor }) => {
          const pos = findUploadPlaceholderPos(state, id);
          if (pos === null) return false;
          const imageType = editor.schema.nodes['image'];
          if (!imageType) return false;
          if (dispatch) {
            // Same-transaction remove-widget + insert-node so the user sees a single atomic
            // swap instead of a flicker between widget gone and node arrived.
            const removeMeta: RemoveMeta = { kind: 'remove', id };
            tr.setMeta(PLUGIN_KEY, removeMeta);
            tr.insert(pos, imageType.create(attrs));
            dispatch(tr);
          }
          return true;
        },

      removeUploadPlaceholder:
        ({ id }) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const meta: RemoveMeta = { kind: 'remove', id };
            tr.setMeta(PLUGIN_KEY, meta);
            dispatch(tr);
          }
          return true;
        },
    };
  },
});

function buildUploadPlaceholderPlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: PLUGIN_KEY,
    state: {
      init: () => DecorationSet.empty,
      apply: (tr: Transaction, set: DecorationSet) => {
        // First map the existing set through the transaction — this is the call that gives
        // placeholders position-stability across typing, deletes, and other edits.
        let next = set.map(tr.mapping, tr.doc);
        const meta = tr.getMeta(PLUGIN_KEY) as PluginMeta | undefined;
        if (!meta) return next;
        if (meta.kind === 'add') {
          // `side: -1` keeps placeholders on the left of the cursor when text is typed at the
          // same position, mirroring how the eventual image would inherit the position before
          // any new text the user types after the drop.
          const deco = Decoration.widget(meta.pos, () => renderPlaceholder(meta.spec), {
            side: -1,
            id: meta.spec.id,
            fileName: meta.spec.fileName,
            previewUrl: meta.spec.previewUrl,
          });
          next = next.add(tr.doc, [deco]);
        } else if (meta.kind === 'remove') {
          const toRemove = next.find(
            undefined,
            undefined,
            (s) => (s as UploadPlaceholderSpec).id === meta.id,
          );
          if (toRemove.length > 0) next = next.remove(toRemove);
        }
        return next;
      },
    },
    props: {
      decorations: (state) => PLUGIN_KEY.getState(state) ?? DecorationSet.empty,
    },
  });
}
